/** Haversine distance in kilometers between two lat/lng points. */
export function haversineKm(a, b) {
  const R = 6371;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function pathDistanceKm(points) {
  let total = 0;
  for (let i = 0; i < points.length - 1; i += 1) {
    total += haversineKm(points[i], points[i + 1]);
  }
  return total;
}

function permutations(arr) {
  if (arr.length <= 1) return [arr];
  const result = [];
  for (let i = 0; i < arr.length; i += 1) {
    const rest = [...arr.slice(0, i), ...arr.slice(i + 1)];
    permutations(rest).forEach((p) => result.push([arr[i], ...p]));
  }
  return result;
}

function twoOptImprove(start, end, order) {
  let best = [...order];
  let improved = true;
  while (improved) {
    improved = false;
    for (let i = 0; i < best.length - 1; i += 1) {
      for (let j = i + 1; j < best.length; j += 1) {
        const candidate = [
          ...best.slice(0, i),
          ...best.slice(i, j + 1).reverse(),
          ...best.slice(j + 1),
        ];
        if (pathDistanceKm([start, ...candidate, end]) < pathDistanceKm([start, ...best, end])) {
          best = candidate;
          improved = true;
        }
      }
    }
  }
  return best;
}

function nearestNeighborOrder(start, end, waypoints) {
  const remaining = [...waypoints];
  const order = [];
  let current = start;
  while (remaining.length) {
    let bestIdx = 0;
    let bestDist = Infinity;
    for (let i = 0; i < remaining.length; i += 1) {
      const d = haversineKm(current, remaining[i]);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }
    const next = remaining.splice(bestIdx, 1)[0];
    order.push(next);
    current = next;
  }
  return twoOptImprove(start, end, order);
}

/**
 * Fixed-start / fixed-end TSP: visit every waypoint once, minimizing total distance.
 * @param {{ lat: number, lng: number }} start
 * @param {{ lat: number, lng: number }} end
 * @param {Array<{ id: string, lat: number, lng: number, label?: string }>} waypoints
 */
export function solveFixedEndpointsTsp(start, end, waypoints) {
  const n = waypoints.length;
  if (n === 0) {
    const path = [start, end];
    return { order: [], path, totalKm: pathDistanceKm(path) };
  }

  let order;
  if (n <= 9) {
    let best = null;
    let bestDist = Infinity;
    permutations(waypoints).forEach((perm) => {
      const dist = pathDistanceKm([start, ...perm, end]);
      if (dist < bestDist) {
        bestDist = dist;
        best = perm;
      }
    });
    order = best;
  } else {
    order = nearestNeighborOrder(start, end, waypoints);
  }

  const path = [start, ...order, end];
  return {
    order,
    path,
    totalKm: pathDistanceKm(path),
  };
}

export function kmToMiles(km) {
  return km * 0.621371;
}
